import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDropzone } from "react-dropzone";
import { CloudUpload, Loader2, Play, X } from "lucide-react";
import {
  resolveAgentQuery,
  resolveAutomationQuery,
  resolveAutomationContextObject,
  resolveAgentVariablesObject,
  serializeAutomationJsonField,
} from "./automationAgentConfig";
import { fetchAutomationById } from "../../services/automationService";
import { fetchAgentById } from "../../utils/agentVariables";
import axiosInstance from "../../utils/api";
import "./Automation.css";
import "./StartAutomationModal.css";

const humanizeFieldKey = (key) =>
  key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const StartAutomationModal = ({
  show,
  onHide,
  automation = null,
  agent = null,
  isRunning = false,
  onSubmit,
}) => {
  const isAgent = Boolean(agent);
  const [automationRecord, setAutomationRecord] = useState(null);
  const [variableFields, setVariableFields] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const title = "Start Automation";
  const subtitle = isAgent
    ? agent?.name ?? (agent?.id != null ? `Agent #${agent.id}` : "Agent")
    : automation?.workflow_name ??
      automation?.name ??
      (automation?.id != null ? `Automation #${automation.id}` : "Automation");

  const fieldKeys = Object.keys(variableFields);

  useEffect(() => {
    if (!show) return;

    let cancelled = false;

    if (agent) {
      setAutomationRecord(null);
      setVariableFields(resolveAgentVariablesObject(agent));
      setUploadedFiles([]);

      fetchAgentById(axiosInstance, agent.id)
        .then((fullAgent) => {
          if (cancelled || !fullAgent) return;
          setVariableFields(
            resolveAgentVariablesObject({ ...agent, fullData: fullAgent })
          );
        })
        .catch((error) => {
          console.error("Failed to load agent variables:", error);
        });

      return () => {
        cancelled = true;
      };
    }

    if (!automation) return;

    const applyAutomationRecord = (record) => {
      if (cancelled || !record) return;
      setAutomationRecord(record);
      setVariableFields(resolveAutomationContextObject(record));
    };

    applyAutomationRecord(automation);
    setUploadedFiles([]);

    fetchAutomationById(automation.id)
      .then((fullAutomation) => {
        if (cancelled || !fullAutomation) return;
        applyAutomationRecord(fullAutomation);
      })
      .catch((error) => {
        console.error("Failed to load automation details:", error);
      });

    return () => {
      cancelled = true;
    };
  }, [show, automation, agent]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "application/pdf": [".pdf"],
      "text/plain": [".txt"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
        ".docx",
      ],
      "text/csv": [".csv"],
      "image/png": [".png"],
      "image/jpeg": [".jpg", ".jpeg"],
    },
    onDrop: (acceptedFiles) => {
      setUploadedFiles((prev) => [...prev, ...acceptedFiles]);
    },
    disabled: isRunning,
  });

  const removeFile = (index) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const updateVariableField = (key, value) => {
    setVariableFields((prev) => ({ ...prev, [key]: value }));
  };

  const handlePrimaryAction = () => {
    if (isRunning) return;
    if (isAgent && !agent) return;
    if (!isAgent && !automation) return;

    const resolvedQuery = isAgent
      ? resolveAgentQuery(agent)
      : resolveAutomationQuery(automationRecord ?? automation);

    onSubmit({
      mode: "manual",
      query: resolvedQuery,
      variables: serializeAutomationJsonField(variableFields),
      files: uploadedFiles,
    });
  };

  return (
    <Modal
      show={show}
      onHide={isRunning ? undefined : onHide}
      centered
      className="start-automation-modal"
      dialogClassName="start-automation-modal-dialog"
      contentClassName="start-automation-modal-content"
      backdrop={isRunning ? "static" : true}
      keyboard={!isRunning}
    >
      <Modal.Header
        closeButton={false}
        className="start-automation-modal-header"
      >
        <div>
          <Modal.Title>{title}</Modal.Title>
          <p className="start-automation-modal-subtitle">{subtitle}</p>
        </div>
        <button
          type="button"
          className="start-automation-modal-close"
          onClick={onHide}
          disabled={isRunning}
          aria-label="Close"
        >
          <X size={18} />
        </button>
      </Modal.Header>

      <Modal.Body className="start-automation-modal-body">
        {fieldKeys.length > 0 && (
          <div className="start-automation-context-fields">
            {fieldKeys.map((key) => (
              <div key={key} className="start-automation-field">
                <label htmlFor={`start-automation-field-${key}`}>
                  {humanizeFieldKey(key)}
                </label>
                <input
                  id={`start-automation-field-${key}`}
                  type="text"
                  className="start-automation-context-input"
                  value={variableFields[key] ?? ""}
                  onChange={(e) => updateVariableField(key, e.target.value)}
                  disabled={isRunning}
                />
              </div>
            ))}
          </div>
        )}

        <section className="start-automation-section">
          <h3 className="start-automation-section-title">Documents</h3>
          <div
            {...getRootProps({
              className: `start-automation-upload-zone${
                isDragActive ? " start-automation-upload-zone--active" : ""
              }`,
            })}
          >
            <input {...getInputProps()} disabled={isRunning} />
            <div className="start-automation-upload-icon-wrap">
              <CloudUpload size={32} className="start-automation-upload-icon" />
            </div>
            <p className="start-automation-upload-text">
              Drag & drop files here, or click to browse
            </p>
            <p className="start-automation-upload-hint">PDF, images, CSV, DOC</p>
          </div>

          {uploadedFiles.length > 0 && (
            <ul className="start-automation-file-list">
              {uploadedFiles.map((file, index) => (
                <li
                  key={`${file.name}-${index}`}
                  className="start-automation-file-item"
                >
                  <span className="start-automation-file-name">{file.name}</span>
                  <button
                    type="button"
                    className="start-automation-file-remove"
                    onClick={() => removeFile(index)}
                    disabled={isRunning}
                    aria-label={`Remove ${file.name}`}
                  >
                    <X size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </Modal.Body>

      <Modal.Footer className="start-automation-modal-footer">
        <button
          type="button"
          className="automation-btn-secondary"
          onClick={onHide}
          disabled={isRunning}
        >
          Cancel
        </button>
        <button
          type="button"
          className="automation-btn-primary"
          onClick={handlePrimaryAction}
          disabled={isRunning || (isAgent && !agent) || (!isAgent && !automation)}
        >
          {isRunning ? (
            <>
              <Loader2 size={16} className="automation-spin" />
              Running...
            </>
          ) : (
            <>
              <Play size={16} />
              Run
            </>
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default StartAutomationModal;
